import { parseAsString, useQueryState } from 'nuqs';
import type { PlatformPage, UsageRow } from '@barbeiropro/contracts';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DataTable, type DataColumn } from '@/components/shared/data-table';
import { PageHeader } from '@/components/shared/page-header';
import { useTableParams } from '@/components/shared/use-table-params';
import { Pill } from '@/features/finance/pill';
import { DownloadButton, microsMoney, useList } from './shared';

function months() {
  const d = new Date();
  return Array.from({ length: 12 }, (_, i) => {
    const x = new Date(d.getFullYear(), d.getMonth() - i - 1, 1);
    return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}`;
  });
}

export function UsagePage() {
  const params = useTableParams({ sort: 'organizationName' });
  const [period, setPeriod] = useQueryState('periodo', parseAsString.withDefault(''));
  const query = { ...params.query, period: period || undefined };
  const list = useList<PlatformPage<UsageRow>>('usage', '/platform/usage', query);
  const n = (v: number) => v.toLocaleString('pt-BR');
  const columns: DataColumn<UsageRow>[] = [
    { id: 'organizationName', header: 'Barbearia', meta: { sortKey: 'organizationName' }, cell: ({ row }) => (<span><span className="font-medium">{row.original.organizationName}</span><span className="block text-xs text-muted-foreground">{row.original.planName ?? 'Sem plano'}</span></span>) },
    { id: 'users', header: 'Usuários', meta: { sortKey: 'users', align: 'right' }, cell: ({ row }) => n(row.original.users) },
    { id: 'units', header: 'Unidades', meta: { sortKey: 'units', align: 'right' }, cell: ({ row }) => n(row.original.units) },
    { id: 'appointmentsMonth', header: 'Agendamentos', meta: { sortKey: 'appointmentsMonth', align: 'right' }, cell: ({ row }) => n(row.original.appointmentsMonth) },
    { id: 'storageMb', header: 'Armazenamento (MB)', meta: { sortKey: 'storageMb', align: 'right' }, cell: ({ row }) => n(row.original.storageMb) },
    { id: 'aiRequestsMonth', header: 'Requisições de IA', meta: { sortKey: 'aiRequestsMonth', align: 'right' }, cell: ({ row }) => n(row.original.aiRequestsMonth) },
    { id: 'aiCostMicros', header: 'Custo de IA', meta: { sortKey: 'aiCostMicros', align: 'right' }, cell: ({ row }) => microsMoney(row.original.aiCostMicros) },
    { id: 'near', header: 'Limite', cell: ({ row }) => (row.original.nearLimit ? <Pill tone="warn">Perto do limite</Pill> : <span className="text-muted-foreground">OK</span>) },
  ];
  return (
    <div>
      <PageHeader title="Uso e IA" description="Consumo por barbearia no período e custo das requisições de IA." actions={<DownloadButton path="/platform/usage/export" query={query} filename="uso.xlsx" />} />
      <DataTable
        columns={columns}
        data={list.data}
        params={params}
        isLoading={list.isLoading}
        error={list.error}
        onRetry={list.refetch}
        searchPlaceholder="Buscar por barbearia"
        empty={{ title: 'Sem dados de uso neste período' }}
        filters={
          <div className="flex items-center gap-2">
            <Label htmlFor="u-period" className="text-sm">Mês</Label>
            <Select value={period || '_now'} onValueChange={(v) => { void setPeriod(v === '_now' ? '' : v); params.set({ page: 1 }); }}>
              <SelectTrigger id="u-period" size="sm" className="w-44"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_now">Mês atual</SelectItem>
                {months().map((m) => (<SelectItem key={m} value={m}>{m.slice(5)}/{m.slice(0, 4)}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
        }
      />
    </div>
  );
}
